import React, { useState, useEffect } from "react";

import mainStyle from "../../mainStyle.module.scss";

import useDataSourceManager from "hooks/useDataSourceManager";
import useAuthScope from "hooks/useAuthScope";

import { v4 as uuidv4 } from "uuid";

import { useTheme, styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";

import CheckboxField from "components/CheckboxField";
import { SolidButton } from "components/buttons/SolidButton";
import { OutlineButton } from "components/buttons/OutlineButton";

import { ReactComponent as SwitchOnIcon } from "assets/images/icons/Toggle-On.svg";
import { ReactComponent as SwitchOffIcon } from "assets/images/icons/Toggle-Off.svg";

const StyledIconButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.primary.transparent,
  width: "40px",
  height: "22px",
  minWidth: "16px",
  padding: "0",
  boxShadow: "none",
  "&:hover": {
    backgroundColor: theme.palette.primary.transparent,
  },
}));

const Notifications = ({ showEditGeneral }) => {
  const theme = useTheme();
  const { auth, standalone, isAuthReady } = useAuthScope();

  const [readNotificationUuid, _setReadNotificationUuid] = useState(uuidv4());
  const [updateNotificationUuid, _setUpdateNotificationUuid] = useState(uuidv4());

  const [originalNotificationData, setOriginalNotificationData] = useState({
    enable: null,
    deviceOffline: null,
    deviceFirmwareUpdate: null,
    deviceRegistration: null,
  })
  const [currentNotificationData, setCurrentNotificationData] = useState({
    enable: null,
    deviceOffline: null,
    deviceFirmwareUpdate: null,
    deviceRegistration: null,
  })

  function handleChange(key, value) {
    setCurrentNotificationData(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const { dataMap, getData, sendRequest } = useDataSourceManager();

  useEffect(() => {
    if (isAuthReady) {
      const baseUrl = standalone ? `/orgs/:orgId` : `/ents/:entId/orgs/:orgId`;

      const apiConfig = {
        org: `${baseUrl}/settings/notification.read`,
        site: `${baseUrl}/sites/:siteId/settings/notification.read`
      };

      const restfulParamsConfig = {
        standalone: {
          orgId: auth.realm?.id,
          siteId: auth.sites?.id,
        },
        nonStandalone: {
          entId: auth.realm?.id,
          orgId: auth.orgs?.id,
          siteId: auth.sites?.id,
        },
      };

      const apiKey = apiConfig[auth.scopeLevel];
      const mode = standalone ? "standalone" : "nonStandalone";
      const restfulParams = restfulParamsConfig[mode];

      sendRequest(apiKey, readNotificationUuid, restfulParams);
    }
  }, [isAuthReady])

  function updateSetting() {
    const baseUrl = standalone ? `/orgs/:orgId` : `/ents/:entId/orgs/:orgId`;

    const apiConfig = {
      org: `${baseUrl}/settings/notification.update`,
      site: `${baseUrl}/sites/:siteId/settings/notification.update`
    };

    const restfulParamsConfig = {
      standalone: {
        orgId: auth.realm?.id,
        siteId: auth.sites?.id,
      },
      nonStandalone: {
        entId: auth.realm?.id,
        orgId: auth.orgs?.id,
        siteId: auth.sites?.id,
      },
    };

    const apiKey = apiConfig[auth.scopeLevel];
    const mode = standalone ? "standalone" : "nonStandalone";
    const restfulParams = restfulParamsConfig[mode];

    sendRequest(apiKey, updateNotificationUuid, restfulParams, { data: { ...currentNotificationData } });
  }

  useEffect(() => {
    const readResponse = getData(readNotificationUuid);
    if (readResponse && readResponse.data) {
      const data = readResponse.data
      setOriginalNotificationData(data)
      setCurrentNotificationData(data)
    }

    const updateResponse = getData(updateNotificationUuid);
    if (updateResponse && updateResponse.data) {
      const data = updateResponse.data;
      setOriginalNotificationData(data)
      setCurrentNotificationData(data)
    }
  }, [dataMap])

  return (
    <Box>
      <Typography sx={{
        mb: 2,
        ml: 1,
        mt: 2,
        color: theme.palette.primary.main,
        fontFamily: theme.typography.fontFamily,
        fontSize: theme.typography.customFontSize[5],
        fontWeight: theme.typography.customFontWeight[3],
      }}>
        Notification Settings
      </Typography>
      <Divider />
      <div className={mainStyle["contentArea"]}>
        <Grid container className={`${mainStyle["optionArea"]}`}>
          <Typography
            sx={{
              width: "190px",
              color: theme.palette.primary.greyPrimary,
              fontSize: theme.typography.customFontSize[2],
              fontWeight: theme.typography.customFontWeight[1],
            }}
          >
            Email Notifications
          </Typography>
          <StyledIconButton
            onClick={() => setCurrentNotificationData((pre) => ({ ...pre, enable: !pre.enable, }))}
            disabled={!showEditGeneral}
          >
            {currentNotificationData.enable ? <SwitchOnIcon /> : <SwitchOffIcon />}
          </StyledIconButton>
        </Grid>

        <Grid container className={`${mainStyle["optionArea"]}`}>
          <Typography
            sx={{
              color: theme.palette.primary.greyPrimary,
              fontSize: theme.typography.customFontSize[2],
            }}
          >
            Enable Email notification to your account registered E-mail address.
          </Typography>
        </Grid>
        <Divider />

        <Grid container className={`${mainStyle["optionArea"]}`}>
          <Box sx={{ padding: "20px" }}>
            <Typography
              sx={{
                width: "190px",
                color: theme.palette.primary.greyPrimary,
                fontSize: theme.typography.customFontSize[2],
                fontWeight: theme.typography.customFontWeight[1],
              }}
            >
              Notification Events
            </Typography>
            {
              auth.scopeLevel === 'org' && <Typography
                sx={{
                  color: theme.palette.primary.greyPrimary,
                  fontSize: theme.typography.customFontSize[2],
                }}
              >
                Notifications only apply to events of the organization, not to events within its sites.
              </Typography>
            }
            <Box sx={{ marginTop: '20px' }}>
              <CheckboxField
                label="Device Offline"
                checked={currentNotificationData.deviceOffline}
                onChange={(value) => handleChange("deviceOffline", value)}
                disabled={!showEditGeneral}
              />
            </Box>
            <Box sx={{ marginTop: '20px' }}>
              <CheckboxField
                label="Device Registration"
                checked={currentNotificationData.deviceRegistration}
                onChange={(value) => handleChange("deviceRegistration", value)}
                disabled={!showEditGeneral}
              />
            </Box>
            <Box sx={{ marginTop: '20px' }}>
              <CheckboxField
                label="Firmware Update"
                checked={currentNotificationData.deviceFirmwareUpdate}
                onChange={(value) => handleChange("deviceFirmwareUpdate", value)}
                disabled={!showEditGeneral}
              />
            </Box>
          </Box>
        </Grid>
        {
          auth && auth.permissions !== "Viewer" &&
          <Grid
            container
            style={{ justifyContent: "center", width: "100%", marginTop: "250px" }}
          >
            <Box style={{ margin: "10px" }}>
              <OutlineButton
                width="135px"
                height="42px"
                onClick={() => setCurrentNotificationData(originalNotificationData)}
              >
                Cancel
              </OutlineButton>
            </Box>
            <Box style={{ margin: "10px" }}>
              <SolidButton
                width="135px"
                height="42px"
                onClick={updateSetting}
              >
                Save
              </SolidButton>
            </Box>
          </Grid>
        }
      </div>
    </Box>
  );
}

export default Notifications
